import type { ReactNode } from 'react'

import styled from '@emotion/styled'
import type { Root } from 'mdast'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { connect } from 'scrivito'

import { useConversationStore } from '../core/store/useConversationStore'
import { theme } from '@/Atomic/assets/theme'
import { DataItemLink } from '@/Atomic/Atoms/DataItemLink'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'

// Pattern: /_data-item/<Type>/<id> — tolerates trailing query/hash so external links don't break extraction.
const DATA_ITEM_PATTERN = /^\/_data-item\/(?<type>[^/]+)\/(?<id>[^/?#]+)/

// Remark plugin: removes hr nodes at the start or end of the document
function remarkStripEdgeHr() {
  return (tree: Root) => {
    while (
      tree.children.length > 0 &&
      tree.children[0]?.type === 'thematicBreak'
    ) {
      tree.children.shift()
    }
    while (
      tree.children.length > 0 &&
      tree.children.at(-1)?.type === 'thematicBreak'
    ) {
      tree.children.pop()
    }
  }
}

// --- Styled components ---

// Wraps all markdown output — owns layout rules for run-prompt buttons and plain links
const MarkdownContent = styled.div`
  // Paragraphs containing run-prompt buttons become flex columns
  p:has([data-run-prompt]) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.space.xs};
    margin: 0;

    [data-run-prompt] {
      margin-bottom: ${theme.space.xs};
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  // Lists containing run-prompt buttons lose their bullets
  ul:has([data-run-prompt]) {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.space.xs};
  }

  // Plain links from the AI (not run-prompt, not DataItemLink)
  a[href]:not([data-run-prompt]) {
    color: ${theme.colors.link.default};
    text-decoration: underline;
  }

  // Section dividers — subtle
  hr {
    border: none;
    border-top: 1px solid ${theme.colors.border.line};
    margin: ${theme.space.s} 0;
  }

  // GFM table styles
  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.875rem;
    margin: 0.5em 0;
  }
  th,
  td {
    border: 1px solid ${theme.colors.border.stroke};
    padding: ${theme.space.xs} ${theme.space.s};
    text-align: left;
  }
  th {
    background: ${theme.colors.background.subtle};
    font-weight: 600;
  }

  // Reset heading sizes to body size — prevents flash of browser-default h1/h2
  // sizes during typewriter streaming. Visual hierarchy via weight + spacing only.
  h1,
  h2,
  h3,
  h4 {
    font-size: 0.875rem;
    font-weight: 600;
    margin: ${theme.space.s} 0 ${theme.space.xs};
    line-height: inherit;
  }
`

// Mirrors NeoCRM Badge language: square 3px radius, tinted background, no border.
// Tappable variant: more padding, sentence-case text, normal weight.
const RunPromptButton = styled.button`
  display: inline-flex;
  align-items: center;
  line-height: 1.4;
  border-radius: 3px;
  padding: ${theme.space.xs} ${theme.space.s};
  background: ${theme.colors.background.subtle};
  border: none;
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  width: fit-content;
  text-align: left;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${theme.colors.background.active};
  }
`

const StyledDataItemLink = styled(DataItemLink)`
  color: ${theme.colors.link.default};
  text-decoration: underline;
`

// Underlined placeholder rendered for complete links while streaming.
// No cursor/click — upgrades to a real link when streaming ends.
const ProtoLink = styled.span`
  color: ${theme.colors.link.default};
  text-decoration: underline;
`

// --- Sub-component: DataItemLink renderer (Scrivito reactive) ---

interface DataItemLinkRendererProps {
  type: string
  id: string
  children: ReactNode
}

// Inferred from DataItemLink props so we avoid a direct scrivito import for the type.
type DataItemProp = Parameters<typeof DataItemLink>[0]['dataItem']

const DataItemLinkRenderer = connect(function DataItemLinkRenderer({
  type,
  id,
  children,
}: DataItemLinkRendererProps) {
  const rawItem = getHostRuntime().data.getDataClassFor(type)?.get(id) ?? null
  // warning: DataItem is not publicly exported by Scrivito; the adapter returns
  // items as unknown. Type is inferred from DataItemLink's prop signature to
  // avoid a direct scrivito import — forced by Scrivito's missing public export.
  const dataItem = rawItem as DataItemProp
  return <StyledDataItemLink dataItem={dataItem}>{children}</StyledDataItemLink>
})

// --- Main component ---

// During streaming: replace complete link URLs with a sentinel so they render
// as underlined proto-links immediately (no DOM transition, no Scrivito mount).
// Incomplete link fragments at the tail are stripped entirely.
// When streaming ends, safeContent === content and all links resolve normally.
const PROTO_LINK = '#ai-chatbot-proto-link'

function prepareForStreaming(text: string): string {
  // Complete links: preserve text, replace URL with sentinel
  const withProto = text.replace(
    /\[([^\]]*)\]\([^)]*\)/g,
    `[$1](${PROTO_LINK})`,
  )
  // Strip any trailing in-progress link fragment (single [, [text, [text](partial)
  return withProto.replace(/\[.*$/, '')
}

interface MarkdownRendererProps {
  content: string
  isStreaming?: boolean
}

export function MarkdownRenderer({
  content,
  isStreaming,
}: MarkdownRendererProps) {
  const { submit } = useConversationStore.getState()
  const safeContent = isStreaming ? prepareForStreaming(content) : content
  return (
    <MarkdownContent>
      <ReactMarkdown
        // singleTilde: false — GFM treats `~x~` as strikethrough by default, so a
        // pair of approximation tildes ("total ~€123.58M ... at ~€123.46M") struck
        // through everything between them. Only `~~x~~` marks strikethrough now.
        remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkStripEdgeHr]}
        components={{
          a({ href, children }) {
            // Proto-links: complete links during streaming use sentinel href.
            // Render as underlined non-clickable span — no Scrivito mount, no
            // DOM transition. Upgrades to a real link when streaming ends.
            if (href === PROTO_LINK) {
              return <ProtoLink>{children}</ProtoLink>
            }

            // #run-prompt links → clickable button that re-fires the prompt
            if (href === '#run-prompt') {
              const text = extractText(children)
              return (
                <RunPromptButton
                  data-run-prompt=""
                  onClick={() => submit(text)}
                  type="button"
                >
                  {children}
                </RunPromptButton>
              )
            }

            // /_data-item/<Type>/<id> → DataItemLink for SPA navigation
            if (href) {
              const match = DATA_ITEM_PATTERN.exec(href)
              const type = match?.groups?.type
              const id = match?.groups?.id
              if (type && id) {
                return (
                  <DataItemLinkRenderer type={type} id={id}>
                    {children}
                  </DataItemLinkRenderer>
                )
              }
            }

            // Block javascript: and data: URIs — model output must not execute code.
            if (href && /^(javascript|data):/i.test(href)) {
              return <>{children}</>
            }

            // Internal app links (same origin) — navigate via Scrivito SPA, keep bot open.
            // Reject protocol-relative URLs (//host) — they resolve to a foreign origin.
            if (href) {
              const isSameOrigin =
                (href.startsWith('/') && !href.startsWith('//')) ||
                href.startsWith(window.location.origin)
              if (isSameOrigin) {
                return (
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault()
                      getHostRuntime().navigation.navigate({ url: href })
                    }}
                  >
                    {children}
                  </a>
                )
              }
            }

            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            )
          },
        }}
      >
        {safeContent}
      </ReactMarkdown>
    </MarkdownContent>
  )
}

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join('')
  return ''
}
